import { DisplayValueFormatterProps, GenericFlyoutListMenuWidget, GenericMenuItemInfo } from '@tableau/widgets';
import * as React from 'react';
import { classes, forceRenderStyles } from 'typestyle';
import {
  dropdownMenuStyles,
  MenuItemContainerStyleName,
  MenuItemDescriptionStyleName,
  MenuItemHeaderStyleName,
  MenuItemLabelStyleName,
  MenuItemPrecedingIconStyleName,
  MenuItemStyleName,
  overflowDropdownStyleName,
} from '../styles/DropdownStyles';
import { GenericToolbarItem } from '../types/GenericToolbarItem';
import { Button, ButtonProps } from './Button';

export interface DropdownProps
  extends Pick<ButtonProps, 'disabled' | 'iconId' | 'id' | 'isHidden' | 'label' | 'omitIcon' | 'showLabel' | 'tabIndex' | 'testId'> {
  /** Items to display in the dropdown menu */
  dropdownItems: GenericToolbarItem[];
  /** Set to true for overflow dropdown styling */
  isOverflow?: boolean;
}

export function Dropdown(props: DropdownProps): React.ReactElement {
  const [buttonEl, setButtonEl] = React.useState<HTMLButtonElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const menuId = `viz-viewer-toolbar-${props.id}-menu`;

  const { dropdownItems, isOverflow, ...buttonProps } = props;

  const menuItemInfos = React.useMemo(() => {
    // if one item is marked as selected, we consider the list to contain selectable items
    const showSelectedState = dropdownItems.some((item) => item.selected);
    // if any item has as a description, the list needs bold labels so that label and description are visually different
    const showBoldLabels = dropdownItems.some((item) => item.description);
    return dropdownItems.map((item) => convertToolbarItemToMenuItemInfo(item, showSelectedState, showBoldLabels, isOverflow));
  }, [dropdownItems, isOverflow]);

  const handleButtonClick = (): void => setIsMenuOpen(!isMenuOpen);
  const handleMenuClose = (): void => setIsMenuOpen(false);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      setIsMenuOpen(true);
    }
  };

  const handleSelect = (value: string): void => {
    dropdownItems.forEach((item) => {
      if (item.testId === value && !item.disabled) {
        setIsMenuOpen(false);
        item.onClick();
      }
    });
  };

  // make sure dropdown menu is closed when entering/exiting fullscreen
  React.useEffect(() => {
    document.addEventListener('fullscreenchange', handleMenuClose);
    return (): void => document.removeEventListener('fullscreenchange', handleMenuClose);
  }, []);

  if (dropdownItems.length === 0) {
    return <></>;
  }

  return (
    <>
      <Button
        {...buttonProps}
        aria-controls={isMenuOpen ? menuId : undefined}
        aria-haspopup={'menu'}
        aria-expanded={isMenuOpen}
        buttonRef={setButtonEl}
        buttonType={'dropdown'}
        className={isOverflow ? overflowDropdownStyleName : undefined}
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        selected={isMenuOpen}
      />
      {isMenuOpen && (
        <GenericFlyoutListMenuWidget
          aria-labelledby={buttonProps.id}
          allowAutoPosition={true}
          anchorEl={buttonEl}
          id={menuId}
          menuItemInfos={menuItemInfos}
          menuStyle={{ ...dropdownMenuStyles, minWidth: buttonEl!.getBoundingClientRect().width }}
          onSelect={handleSelect}
          requestCloseAll={handleMenuClose}
          testId={`${props.testId}-flyout`}
          toggleEl={buttonEl}
        />
      )}
    </>
  );
}

const convertToolbarItemToMenuItemInfo = (
  toolbarItem: GenericToolbarItem,
  showSelectedState: boolean,
  showBoldLabels: boolean,
  hideDescription?: boolean
): GenericMenuItemInfo => ({
  checked: showSelectedState ? toolbarItem.selected : undefined,
  displayFormatterBoundWithData: getMenuItemFormatter(toolbarItem, showSelectedState, showBoldLabels, hideDescription),
  disabled: toolbarItem.isHeader || toolbarItem.disabled,
  keyboardAccessibleValue: toolbarItem.label,
  value: toolbarItem.testId,
  isGroup: toolbarItem.isHeader,
});

const getMenuItemFormatter = (
  toolbarItem: GenericToolbarItem,
  showSelectedState: boolean,
  showBoldLabels: boolean,
  hideDescription?: boolean
): React.ComponentType<DisplayValueFormatterProps> =>
  function toolbarMenuItemFormatter(props: DisplayValueFormatterProps): JSX.Element {
    const containerClassName = classes(
      toolbarItem.isHeader ? MenuItemHeaderStyleName : MenuItemContainerStyleName(toolbarItem.iconId, !!toolbarItem.selected),
      props.isHighlighted && 'highlighted',
      props.active && 'active',
      props.disabled && 'disabled'
    );
    const iconClassName = classes('toolbar-dropdown-menu-item-icon', MenuItemPrecedingIconStyleName);
    const checkedIconClassName = classes('toolbar-dropdown-menu-item-checked', MenuItemPrecedingIconStyleName);
    const labelClassName = classes(MenuItemLabelStyleName, showBoldLabels && 'bold');
    // make sure the styles we generated above are added to the DOM before the menu positioning happens
    forceRenderStyles();

    return (
      <div className={containerClassName}>
        <div className={MenuItemStyleName}>
          {showSelectedState && <span className={checkedIconClassName} />}
          {!showSelectedState && toolbarItem.iconId && <span className={iconClassName} />}
          <label className={labelClassName}>{toolbarItem.label}</label>
        </div>
        {!hideDescription && toolbarItem.description && <div className={MenuItemDescriptionStyleName}>{toolbarItem.description}</div>}
      </div>
    );
  };
